"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import type { ApiWave } from "@/generated/models/ApiWave";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { WaveContent } from "./WaveContent";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useSidebarState } from "../../../hooks/useSidebarState";
import { ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
interface BrainRightSidebarProps {
  readonly waveId: string | null | undefined;
  readonly activeTab: SidebarTab;
  readonly setActiveTab: (tab: SidebarTab) => void;
  readonly variant?: "overlay" | "inline" | undefined;
}
export enum Mode {
  CONTENT = "CONTENT",
  FOLLOWERS = "FOLLOWERS",
}
export enum SidebarTab {
  ABOUT = "ABOUT",
  LEADERBOARD = "LEADERBOARD",
  WINNERS = "WINNERS",
  TOP_VOTERS = "TOP_VOTERS",
  ACTIVITY_LOG = "ACTIVITY_LOG",
}
interface SidebarState {
  readonly wave: ApiWave | undefined;
  readonly mode: Mode;
  readonly setMode: React.Dispatch<React.SetStateAction<Mode>>;
  readonly close: () => void;
}
const useBrainRightSidebarState = waveId => {
  const $ = _c(11);
  const {
    closeRightSidebar
  } = useSidebarState();
  let t0;
  let t1;
  if ($[0] !== waveId) {
    t0 = [QueryKey.WAVE, {
      wave_id: waveId
    }];
    t1 = async () => await commonApiFetch({
      endpoint: `waves/${waveId}`
    });
    $[0] = waveId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!waveId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 60000,
      placeholderData: keepPreviousData
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: wave
  } = useQuery(t3);
  const [mode, setMode] = useState(Mode.CONTENT);
  let t4;
  if ($[7] !== closeRightSidebar || $[8] !== mode || $[9] !== wave) {
    t4 = {
      wave,
      mode,
      setMode,
      close: closeRightSidebar
    };
    $[7] = closeRightSidebar;
    $[8] = mode;
    $[9] = wave;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const SidebarContent = t0 => {
  const $ = _c(6);
  const {
    wave,
    mode,
    setMode,
    activeTab,
    setActiveTab
  } = t0;
  if (!wave) {
    return null;
  }
  let t1;
  if ($[0] !== activeTab || $[1] !== mode || $[2] !== setActiveTab || $[3] !== setMode || $[4] !== wave) {
    t1 = <WaveContent wave={wave} mode={mode} setMode={setMode} activeTab={activeTab} setActiveTab={setActiveTab} />;
    $[0] = activeTab;
    $[1] = mode;
    $[2] = setActiveTab;
    $[3] = setMode;
    $[4] = wave;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const BrainRightSidebar: React.FC<BrainRightSidebarProps> = t0 => {
  const $ = _c(21);
  const {
    waveId,
    activeTab,
    setActiveTab,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "overlay" : t1;
  const {
    wave,
    mode,
    setMode,
    close
  } = useBrainRightSidebarState(waveId);
  const isOverlay = variant === "overlay";
  const t2 = `tw-flex tw-w-[20.5rem] tw-flex-col tw-bg-iron-950 ${isOverlay ? "tw-fixed tw-inset-y-0 tw-right-0 tw-z-[100] tw-border-y-0 tw-border-l tw-border-r-0 tw-border-solid tw-border-iron-800 tw-shadow-2xl lg:tw-bg-opacity-95 lg:tw-backdrop-blur min-[1300px]:tw-bg-opacity-100 min-[1300px]:tw-backdrop-blur-none" : "overflow-hidden tw-max-h-[calc(100vh-16px)] tw-rounded-lg tw-border tw-border-solid tw-border-iron-800"}`;
  let t3;
  if ($[0] !== close || $[1] !== isOverlay) {
    t3 = isOverlay && <div className="tw-absolute -tw-left-5 tw-top-2 tw-z-[110]"><button type="button" onClick={close} className="tw-group tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-r-0 tw-border-solid tw-border-iron-650 tw-bg-iron-700 tw-shadow-[0_8px_20px_rgba(0,0,0,0.35)] tw-transition-all tw-duration-200 desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-600 desktop-hover:hover:tw-shadow-[0_12px_26px_rgba(0,0,0,0.45)]" aria-label="Close sidebar"><ChevronDoubleRightIcon strokeWidth={2} className="tw-size-4 tw-text-iron-200 tw-transition-all tw-duration-200" /></button></div>;
    $[0] = close;
    $[1] = isOverlay;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== activeTab || $[4] !== mode || $[5] !== setActiveTab || $[6] !== setMode || $[7] !== wave) {
    t4 = <div className="tw-h-full tw-overflow-y-auto tw-text-sm tw-text-iron-500 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><SidebarContent wave={wave} mode={mode} setMode={setMode} activeTab={activeTab} setActiveTab={setActiveTab} /></div>;
    $[3] = activeTab;
    $[4] = mode;
    $[5] = setActiveTab;
    $[6] = setMode;
    $[7] = wave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const panel = t5;
  if (isOverlay && typeof document !== "undefined") {
    let t6;
    if ($[13] !== close) {
      t6 = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          close();
        }
      };
      $[13] = close;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== close || $[16] !== t6) {
      t7 = <button type="button" onClick={close} aria-label="Close right overlay" className="tw-fixed tw-inset-0 tw-z-[90] tw-border-0 tw-bg-black/50 focus:tw-outline-none" onKeyDown={t6} />;
      $[15] = close;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== panel || $[19] !== t7) {
      t8 = createPortal(<>{t7}{panel}</>, document.body);
      $[18] = panel;
      $[19] = t7;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  return panel;
};
export default BrainRightSidebar;